import type { ReactNode } from 'react';
import { Button } from '@design-system';
import { missingDetailsCount, type ConsumerApplicationSection } from '@shared/consumerPlan';
import { useApplication } from '../hooks/use-application';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { APPLICATION_SECTION_STEPS } from '../lib/steps';
import { SectionStatusBadge } from './section-status-badge';
import { StepFrame } from './step-frame';

export const APPLICATION_SECTION_TITLES: Record<ConsumerApplicationSection, string> = {
  personal: 'Personal information',
  medical: 'Medical information',
  health: 'Health information',
  beneficiaries: 'Potential beneficiaries',
};

interface ApplicationSectionFrameProps {
  clientId: string;
  section: ConsumerApplicationSection;
  title: string;
  subtitle: string;
  // The section's edited answers, saved when the consumer moves on
  draft: Partial<ConsumerApplicationAnswers>;
  children: ReactNode;
}

/** One application section with the section navigator and save-and-continue. */
export function ApplicationSectionFrame({
  clientId,
  section,
  title,
  subtitle,
  draft,
  children,
}: ApplicationSectionFrameProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { answers, sections, saveAnswers } = useApplication(clientId);
  const index = sections.indexOf(section);
  const nextSection = sections[index + 1];
  const previousSection = sections[index - 1];
  const merged = { ...answers, ...draft };

  const saveAndGo = (target: ConsumerApplicationSection | 'review') => {
    saveAnswers(draft);
    goTo(target === 'review' ? 'application-review' : APPLICATION_SECTION_STEPS[target]);
  };

  return (
    <StepFrame
      step={APPLICATION_SECTION_STEPS[section]}
      title={title}
      subtitle={subtitle}
      onBack={() => back(previousSection ? APPLICATION_SECTION_STEPS[previousSection] : 'application-start')}
      help={
        <>
          <p>Answer what you can. It’s okay to leave gaps; our team will follow up before anything goes to the insurance company.</p>
          <p>Your answers are saved as you move between sections.</p>
        </>
      }
      primary={{ label: 'Save and continue', onClick: () => saveAndGo(nextSection ?? 'review') }}
      next={nextSection ? APPLICATION_SECTION_TITLES[nextSection].toLowerCase() + '.' : 'review your application.'}
    >
      <nav aria-label="Application sections" className="flex flex-wrap gap-2">
        {sections.map((entry, position) => {
          const missing = missingDetailsCount(merged, entry);
          return (
            <Button
              key={entry}
              variant={entry === section ? 'default' : 'outline'}
              size="sm"
              aria-current={entry === section ? 'step' : undefined}
              onClick={() => saveAndGo(entry)}
            >
              {position + 1}. {APPLICATION_SECTION_TITLES[entry]}
              {entry !== section && (
                <SectionStatusBadge missing={missing} />
              )}
            </Button>
          );
        })}
      </nav>
      <div className="flex flex-col gap-4">{children}</div>
    </StepFrame>
  );
}
